import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../lib/NotificationsContext';
import { Layout } from '../components/Layout';
import { cn } from '../lib/utils';
import { 
  Bell, 
  Volume2, 
  VolumeX, 
  CheckCheck, 
  Trash2,
  ArrowLeft,
  Trophy,
  Search,
  Palette,
  ShoppingBag,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Switch } from '../components/ui/switch';

// Get icon for notification type
function getNotificationIcon(phase) {
  const icons = {
    'product_reviews': Search,
    'product_marketing_angles': FileText,
    'ugc_ads': Palette,
    'ugc_full_videos': Palette,
    'ugc_shorts': Palette,
    'winning_product_details': Trophy,
    'winning_product_images': Trophy,
    'static_ads': Palette,
    'shopify': ShoppingBag,
    'shopify_product_images': ShoppingBag,
    'shopify_sections': ShoppingBag
  };
  return icons[phase] || Bell;
}

// Get color for notification type
function getNotificationColor(phase) {
  const colors = {
    'product_reviews': 'bg-blue-100 text-blue-600',
    'product_marketing_angles': 'bg-violet-100 text-violet-600',
    'ugc_ads': 'bg-purple-100 text-purple-600',
    'ugc_full_videos': 'bg-purple-100 text-purple-600',
    'ugc_shorts': 'bg-purple-100 text-purple-600',
    'winning_product_details': 'bg-amber-100 text-amber-600',
    'winning_product_images': 'bg-amber-100 text-amber-600',
    'static_ads': 'bg-orange-100 text-orange-600',
    'shopify': 'bg-green-100 text-green-600',
    'shopify_product_images': 'bg-green-100 text-green-600',
    'shopify_sections': 'bg-green-100 text-green-600'
  };
  return colors[phase] || 'bg-slate-100 text-slate-600';
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    soundEnabled, 
    toggleSound,
    markAsRead,
    markAllAsRead,
    clearAll
  } = useNotifications();

  return (
    <Layout>
      <div data-testid="notifications-page">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h1 className="text-xl font-bold text-[#1E1B4B] font-['Manrope']">
                Notifications
              </h1>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                  {unreadCount} new
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-2 text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-5 h-5" />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Clear all"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sound Toggle */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-cyan-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-slate-400" />
              )}
              <span className="font-medium text-slate-700">Sound Notifications</span>
            </div>
            <Switch
              checked={soundEnabled}
              onCheckedChange={toggleSound}
              className="data-[state=checked]:bg-cyan-500"
            />
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No notifications</h3>
            <p className="text-slate-500 text-sm">
              You&apos;ll see updates when product phases complete
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.phase);
              const colorClass = getNotificationColor(notification.phase);
              
              const isHumanReview = notification.type === 'human_review' && notification.ecomId;

              return (
                <div
                  key={notification.id}
                  className={cn(
                    'p-4 transition-colors',
                    !notification.read && 'bg-cyan-50/50'
                  )}
                >
                  <div className="flex gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      colorClass
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          'text-sm',
                          !notification.read ? 'font-semibold text-[#1E1B4B]' : 'font-medium text-slate-700'
                        )}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-slate-400">
                          {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                        </p>
                        {isHumanReview && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                              navigate(`/confirm-product/${notification.ecomId}`);
                            }}
                            className="px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
                          >
                            Review details
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
